import {ExtendedError} from '../../../libs/shared/helpers/src/types/customErrors';
import {DataGroupModel} from '../../../src/models/api/dataGroupModel';
import {FeatureFlagsEnum} from '../../../src/store/featureFlags/featureFlagsTypes';
import {reportToBugsnag} from '../bugsnag/bugsnag';
import {AppLoadMetricsEnum, logAppLoadMetric} from '../performanceMetrics/appLoadMetric';
import {
  PersistedState,
  PersistedStateProvider,
  PersistedStorageEmptyError,
  PersistedStorageTypeValidationError,
  PersistedStorageUnsupportedError,
} from '../persistentState/persistedState';
import {finishBootAsync} from './bootHelpers';
import {BootResultInternal} from './bootTypes';

export async function bootOfflineAsync(
  persistedStateProvider: PersistedStateProvider,
): Promise<BootResultInternal | undefined> {
  const offlineResult = await beginBootOfflineAsync(persistedStateProvider);
  if (!offlineResult) {
    return undefined;
  }

  return await finishBootOfflineAsync(offlineResult, []);
}

async function beginBootOfflineAsync(provider: PersistedStateProvider): Promise<PersistedState | undefined> {
  const onOfflineStateLoaded = logAppLoadMetric(AppLoadMetricsEnum.OFFLINE_STATE_LOAD);
  try {
    return await provider.loadInitialPersistedStateAsync();
  } catch (error) {
    if (error instanceof PersistedStorageTypeValidationError) {
      provider.tryClearPersistedStateAsync();
      return undefined;
    }

    // We can reasonably expect this to fail due to permissions or not having a persisted state.
    if (error instanceof PersistedStorageEmptyError || error instanceof PersistedStorageUnsupportedError) {
      return undefined;
    }

    // TODO: Log to Datadog?
    reportToBugsnag(new ExtendedError('Failed to boot offline', error));

    // If that failed, clear any offline state for good measure.
    provider.tryClearPersistedStateAsync();

    return undefined;
  } finally {
    onOfflineStateLoaded();
  }
}

async function finishBootOfflineAsync(
  offlineResult: PersistedState,
  extraDataGroups: ReadonlyArray<DataGroupModel>,
): Promise<BootResultInternal> {
  const featureFlags = offlineResult.originalData.config.launchdarkly.flags;
  const hasLazyTeammates = Boolean(featureFlags && featureFlags[FeatureFlagsEnum.LAZY_TEAMMATES]);
  const data = await finishBootAsync(offlineResult.originalData, extraDataGroups, hasLazyTeammates);
  return {
    initialPersistedState: offlineResult,
    initialState: {
      ...offlineResult,
      data,
      originalData: data,
      featureFlags: {
        initial: featureFlags,
        dynamic: featureFlags,
      },
    },
  };
}
